import { Flex, Popconfirm, Tag, Tooltip } from 'antd';
import { AuthorizedIcon, Icon } from '../../../../../components';
import TrashSolidBlack from '../../../../../assets/icons/trash-solid-b-black.svg';
import PenToSquareSolidBlack from '../../../../../assets/icons/pen-to-square-solid-w-black.svg';
import EyeRegularWBlack from '../../../../../assets/icons/eye-regular-w-black.svg';
import Variant99Black from '../../../../../assets/icons/variant99-black.svg';
import FolderOpenRegular from '../../../../../assets/icons/folder-open-regular.svg';
import styles from './index.module.scss';
import { IEnum } from '../../../../../services/type';
import { getValueFromEnum } from '../../../../../utils/functions';
import { ColumnType } from 'antd/es/table';
import { actionTypes } from '../../../../../assets/enums';
import {
  IFile,
  IOrganization,
  IGroupHealthInsuranceDependentsDataSource,
} from '../../../../../types';
import { status_colors } from '../../../../../assets/conts';

export const getColumns = (
  handleClickEye: (id: number) => void,
  handleOpenEditModal: (id: number) => void,
  handleClickDelete: (id: number) => Promise<void>,
  handleChangeCancelledItem: (
    item: IGroupHealthInsuranceDependentsDataSource,
  ) => void,
  handleSetAdditionalFile: (id: number, file: IFile[]) => void,
  organizations: IOrganization[],
  statusChoices: IEnum[],
  policyTypes: IEnum[],
  dependentPayments: IEnum[],
): Array<
  ColumnType<IGroupHealthInsuranceDependentsDataSource> & {
    searchable?: boolean;
  }
> => [
  {
    title: 'Şirket',
    dataIndex: 'organization',
    key: 'organization',
    width: 125,
    searchable: true,
    filters: organizations.map((item) => ({
      text: item.name || '',
      value: item.id,
    })),
    sorter: (a, b) => a.organization - b.organization,
    render: (_, record) => (
      <Tooltip
        title={
          organizations.find((item) => item.id === record.organization)?.name
        }
      >
        <span>{`${organizations.find((item) => item.id === record.organization)?.name.slice(0, 15)}...`}</span>
      </Tooltip>
    ),
  },
  {
    title: 'Bağlı Kişi',
    dataIndex: 'dependent_name',
    key: 'dependent_name',
    searchable: true,
    width: 150,
    sorter: (a, b) => a.dependent_name?.localeCompare(b.dependent_name),
  },
  {
    title: 'Bağımlı Tipi',
    dataIndex: 'dependent_type',
    key: 'dependent_type',
    searchable: true,
    width: 150,
    sorter: (a, b) =>
      a.dependent_type
        ?.toString()
        ?.localeCompare(b.dependent_type?.toString() ?? '') ?? 0,
  },
  {
    title: 'İsim',
    dataIndex: 'first_name',
    key: 'first_name',
    searchable: true,
    width: 150,
    sorter: (a, b) => a.first_name?.localeCompare(b.first_name),
  },
  {
    title: 'Soyisim',
    dataIndex: 'last_name',
    key: 'last_name',
    searchable: true,
    width: 150,
    sorter: (a, b) => a.last_name?.localeCompare(b.last_name),
  },
  {
    title: 'TCKN',
    dataIndex: 'turkish_id',
    key: 'turkish_id',
    searchable: true,
    width: 150,
    sorter: (a, b) => a.turkish_id?.localeCompare(b.turkish_id),
  },
  {
    title: 'Cinsiyet',
    dataIndex: 'gender',
    key: 'gender',
    width: 120,
    filters: [
      { value: 'male', text: 'Erkek' },
      { value: 'female', text: 'Kadın' },
    ],
    filterMode: 'tree',
    filterSearch: true,
    sorter: (a, b) => a.dependent_name?.localeCompare(b.dependent_name),
    render: (_, record) => (
      <div>
        {record.gender === 'male'
          ? 'Erkek'
          : record.gender === 'female'
            ? 'Kadın'
            : ''}
      </div>
    ),
  },
  {
    title: 'Doğum Tarihi',
    dataIndex: 'birthday',
    key: 'birthday',
    width: 150,
    sorter: (a, b) => a.birthday?.localeCompare(b.birthday),
  },
  {
    title: 'GSM No',
    dataIndex: 'gsm',
    key: 'gsm',
    searchable: true,
    width: 150,
    sorter: (a, b) => a.dependent_name?.localeCompare(b.dependent_name),
  },
  {
    title: 'Uyruk',
    dataIndex: 'nationality',
    key: 'nationality',
    filters: [
      { value: 'tc', text: 'TC' },
      { value: 'other', text: 'Diğer' },
    ],
    filterMode: 'tree',
    filterSearch: true,
    width: 150,
    sorter: (a, b) => a.nationality?.localeCompare(b.nationality),
    render: (_: unknown, record: IGroupHealthInsuranceDependentsDataSource) => (
      <div>
        {record.nationality === 'tc'
          ? 'TC'
          : record.nationality === 'other'
            ? 'Diğer'
            : ''}
      </div>
    ),
  },
  {
    title: 'Ödeme',
    dataIndex: 'payment',
    key: 'payment',
    width: 150,
    filters: dependentPayments.map((item) => ({
      text: item.value,
      value: item.key,
    })),
    filterMode: 'tree',
    filterSearch: true,
    sorter: (a, b) => a.payment?.localeCompare(b.payment),
    render: (_: unknown, record: IGroupHealthInsuranceDependentsDataSource) => (
      <Tag className={styles[`${record.payment}Tag`]}>
        {getValueFromEnum(dependentPayments, record.payment)}
      </Tag>
    ),
  },
  {
    title: 'Durum',
    dataIndex: 'recent_status',
    key: 'recent_status',
    width: 150,
    filters: statusChoices.map((item) => ({
      text: getValueFromEnum(statusChoices, item.key) || '',
      value: item.key,
    })),
    filterMode: 'tree',
    filterSearch: true,
    sorter: (a, b) => a.recent_status?.localeCompare(b.recent_status),
    render: (_: unknown, record: IGroupHealthInsuranceDependentsDataSource) => (
      <Tag color={status_colors[record.recent_status]}>
        {getValueFromEnum(statusChoices, record.recent_status)}
      </Tag>
    ),
  },
  {
    title: 'İşlem',
    dataIndex: 'action',
    key: 'action',
    width: 160,
    render: (_: unknown, record: IGroupHealthInsuranceDependentsDataSource) => (
      <Flex justify="flex-end" gap={14}>
        <Icon
          source={EyeRegularWBlack}
          popoverText={actionTypes.VIEW}
          onClick={() => record?.id && handleClickEye(record.id)}
        />
        {record.recent_status !== 'cancelled' && (
          <AuthorizedIcon
            action="add"
            model="grouphealthdependent"
            source={Variant99Black}
            popoverText={actionTypes.CANCEL}
            onClick={() => handleChangeCancelledItem(record)}
          />
        )}
        <Popconfirm
          title={
            <div className={styles.popconfirmTitle}>
              Silme işlemini onaylıyor musunuz?
            </div>
          }
          overlayInnerStyle={{
            width: 150,
            borderColor: '#D6222A',
            borderWidth: 1,
            borderStyle: 'solid',
          }}
          okText="Onayla"
          cancelText="İptal"
          icon={null}
          color="#FAFAFA"
          okButtonProps={{ danger: true, style: { width: '45%' } }}
          cancelButtonProps={{ danger: true, style: { width: '45%' } }}
          onConfirm={() => record.id && handleClickDelete(record.id)}
        >
          <AuthorizedIcon
            action="delete"
            model="grouphealthdependent"
            popoverText={actionTypes.DELETE}
            source={TrashSolidBlack}
          />
        </Popconfirm>
        <AuthorizedIcon
          action="change"
          model="grouphealthdependent"
          popoverText={actionTypes.CHANGE}
          source={PenToSquareSolidBlack}
          onClick={() => record?.id && handleOpenEditModal(record.id)}
        />
        <AuthorizedIcon
          action="view"
          model="grouphealthdependent"
          popoverText={actionTypes.ADDITIONAL_FILE}
          source={FolderOpenRegular}
          onClick={() =>
            handleSetAdditionalFile(record.id, record.additional_files)
          }
        />
      </Flex>
    ),
  },
];
